// src/screens/DealerDashboardScreen.js
import React, { useContext, useEffect, useMemo, useState } from 'react';
import AppHeader from '../components/AppHeader';
import { AuthContext } from '../context/AuthContext';
import { dealerAppointmentsAPI, billingAPI } from '../services/api';
import { Calendar } from 'lucide-react';
import { fetchDashboardData, fetchDealerships } from '../services/dashboardData';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Dashboard Components
import DashboardLayout, { KpiGrid, ChartsGrid } from '../components/dashboard/DashboardLayout';
import KpiCard from '../components/dashboard/KpiCard';
import DailyAppointmentsChart from '../components/dashboard/DailyAppointmentsChart';
import StatusFunnel from '../components/dashboard/StatusFunnel';
import AppointmentsByDayChart from '../components/dashboard/AppointmentsByDayChart';
import TopVehiclesTable from '../components/dashboard/TopVehiclesTable';
import CheckInTimeChart from '../components/dashboard/CheckInTimeChart';

// Dealer Vehicles Components
import DealerVehiclesTable from '../components/dealer/DealerVehiclesTable';
import VehicleCsvUploadModal from '../components/dealer/VehicleCsvUploadModal';

// Billing Component
import DealerBillingScreen from './DealerBillingScreen';

// CRM Settings Component
import DealerCrmSettingsScreen from './DealerCrmSettingsScreen';
import DealerStaffScreen from './DealerStaffScreen';

const statusOptions = ['all', 'Pending', 'Confirmed', 'Vehicle Ready', 'Checked In', 'Completed', 'Canceled', 'No Show'];

// Tabs an email link can open with /dealer?tab=<name> (see the server's
// config/appUrl.js). Admin-only tabs fall back to Appointments for staff.
const LINKABLE_TABS = ['appointments', 'vehicles', 'analytics', 'staff', 'crm', 'billing'];
const ADMIN_ONLY_TABS = ['staff', 'crm', 'billing'];

function initialTab(requested, role) {
  if (!LINKABLE_TABS.includes(requested)) return 'appointments';
  if (ADMIN_ONLY_TABS.includes(requested) && role !== 'dealer_admin') return 'appointments';
  return requested;
}

const DealerDashboardScreen = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Tab state
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => initialTab(searchParams.get('tab'), user?.role));

  // Billing/subscription state
  const [billingStatus, setBillingStatus] = useState(null);
  const [loadingBilling, setLoadingBilling] = useState(true);

  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedDealership, setSelectedDealership] = useState(null);
  const [dealerships, setDealerships] = useState([]);

  // Appointments state (for appointments tab)
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [statusFilter, setStatusFilter] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  // Vehicles tab state
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [vehiclesRefreshKey, setVehiclesRefreshKey] = useState(0);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'dealer_admin' && user.role !== 'dealer_staff' && user.role !== 'admin') {
      navigate('/');
      return;
    }
  }, [user, navigate]);

  // Fetch billing status to check plan limits
  useEffect(() => {
    const fetchBillingStatus = async () => {
      if (!user || (user.role !== 'dealer_admin' && user.role !== 'dealer_staff')) {
        return;
      }

      try {
        setLoadingBilling(true);
        const { data } = await billingAPI.getStatus();
        setBillingStatus(data);
      } catch (err) {
        console.error('Failed to fetch billing status:', err);
        // If billing fetch fails, assume no analytics access for safety
        setBillingStatus({ planLimits: { hasAnalytics: false } });
      } finally {
        setLoadingBilling(false);
      }
    };

    fetchBillingStatus();
  }, [user]);

  // Load dashboard data
  useEffect(() => {
    if (user && (user.role === 'dealer_admin' || user.role === 'dealer_staff' || user.role === 'admin')) {
      // Load mock dashboard data
      const data = fetchDashboardData(selectedDealership);
      setDashboardData(data);

      // Load dealerships list for admin
      if (user.role === 'admin') {
        const dealersList = fetchDealerships();
        setDealerships(dealersList);
      }
    }
  }, [user, selectedDealership]);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (from) params.from = from;
      if (to) params.to = to;

      const { data } = await dealerAppointmentsAPI.list(params);
      setAppointments(data || []);
    } catch (err) {
      console.error('Dealer appointments fetch error', err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to load appointments.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && (user.role === 'dealer_admin' || user.role === 'dealer_staff' || user.role === 'admin') && activeTab === 'appointments') {
      fetchAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, statusFilter, from, to, activeTab]);

  const todayStats = useMemo(() => {
    const today = new Date();
    const isSameDay = (d) => {
      const vd = new Date(d);
      return (
        vd.getFullYear() === today.getFullYear() &&
        vd.getMonth() === today.getMonth() &&
        vd.getDate() === today.getDate()
      );
    };
    const todays = appointments.filter((b) => b.date && isSameDay(b.date));
    return {
      total: todays.length,
      pending: todays.filter((b) => b.status === 'Pending').length,
      confirmed: todays.filter((b) => b.status === 'Confirmed').length,
      vehicleReady: todays.filter((b) => b.status === 'Vehicle Ready').length,
      checkedIn: todays.filter((b) => b.status === 'Checked In').length,
      completed: todays.filter((b) => b.status === 'Completed').length,
    };
  }, [appointments]);

  const allStats = useMemo(() => {
    return {
      pending: appointments.filter((b) => b.status === 'Pending').length,
      confirmed: appointments.filter((b) => b.status === 'Confirmed').length,
      vehicleReady: appointments.filter((b) => b.status === 'Vehicle Ready').length,
      checkedIn: appointments.filter((b) => b.status === 'Checked In').length,
      cancelled: appointments.filter((b) => b.status === 'Canceled').length,
      noShow: appointments.filter((b) => b.status === 'No Show').length,
    };
  }, [appointments]);

  const filteredAppointments = useMemo(() => {
    if (statusFilter === 'all') {
      return appointments.filter((b) => b.status !== 'Canceled' && b.status !== 'No Show');
    }
    return appointments.filter((b) => b.status === statusFilter);
  }, [appointments, statusFilter]);

  const canConfirm = (b) => b.status === 'Pending';
  const canMarkVehicleReady = (b) => b.status === 'Confirmed';
  const canCheckIn = (b) => b.status === 'Vehicle Ready';
  const canComplete = (b) => b.status === 'Checked In';
  const canCancel = (b) => b.status === 'Pending' || b.status === 'Confirmed' || b.status === 'Vehicle Ready';
  const canMarkNoShow = (b) => b.status === 'Confirmed' || b.status === 'Vehicle Ready';

  const updateStatus = async (id, status) => {
    try {
      setUpdatingId(id);
      await dealerAppointmentsAPI.updateStatus(id, status);
      await fetchAppointments();
    } catch (err) {
      console.error('Update status error', err);
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Failed to update status.';
      setError(msg);
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <AppHeader
        title="Dealer Dashboard"
        subtitle={
          user?.role === 'admin'
            ? 'Analytics and insights across all dealerships'
            : 'Analytics and appointment management'
        }
      />

      <div className="flex-1 overflow-y-auto p-4 lg:p-6 lg:pb-6 pb-20 space-y-4 text-xs max-w-7xl lg:mx-auto w-full">
        {error && (
          <div className="bg-red-900/30 text-red-300 px-3 py-2 rounded-xl">
            {error}
          </div>
        )}

        {/* Dealership filter for admin */}
        {user?.role === 'admin' && dealerships.length > 0 && (
          <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary border border-app-border-light dark:border-app-border rounded-2xl p-4">
            <label className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mb-2 block">Filter by Dealership</label>
            <select
              value={selectedDealership || ''}
              onChange={(e) => setSelectedDealership(e.target.value || null)}
              className="w-full lg:w-64 px-3 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-app-text-light dark:text-app-text text-sm"
            >
              <option value="">All Dealerships</option>
              {dealerships.map((dealer) => (
                <option key={dealer.id} value={dealer.id}>
                  {dealer.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-app-border-light dark:border-app-border overflow-x-auto">
          {/* Analytics Tab - Only for plans with analytics access or admins */}
          {(user?.role === 'admin' || billingStatus?.planLimits?.hasAnalytics) && (
            <button
              onClick={() => setActiveTab('analytics')}
              className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 whitespace-nowrap ${
                activeTab === 'analytics'
                  ? 'border-app-accent text-app-accent'
                  : 'border-transparent text-app-text-secondary-light dark:text-app-text-secondary hover:text-app-text-light dark:hover:text-app-text'
              }`}
            >
              Analytics
            </button>
          )}
          <button
            onClick={() => setActiveTab('appointments')}
            className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'appointments'
                ? 'border-app-accent text-app-accent'
                : 'border-transparent text-app-text-secondary-light dark:text-app-text-secondary hover:text-app-text-light dark:hover:text-app-text'
            }`}
          >
            Appointments Management
          </button>
          <button
            onClick={() => setActiveTab('vehicles')}
            className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'vehicles'
                ? 'border-app-accent text-app-accent'
                : 'border-transparent text-app-text-secondary-light dark:text-app-text-secondary hover:text-app-text-light dark:hover:text-app-text'
            }`}
          >
            Vehicles
          </button>
          {/* Calendar Tab - Only for dealer_admin */}
          {user?.role === 'dealer_admin' && (
            <button
              onClick={() => navigate('/dealer/calendar')}
              className="px-4 py-2 text-sm font-semibold transition-colors border-b-2 whitespace-nowrap flex items-center gap-2 border-transparent text-app-text-secondary-light dark:text-app-text-secondary hover:text-app-text-light dark:hover:text-app-text"
            >
              <Calendar size={16} />
              Calendar
            </button>
          )}
          {/* Staff Tab - Only for dealer_admin */}
          {user?.role === 'dealer_admin' && (
            <button
              onClick={() => setActiveTab('staff')}
              className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 whitespace-nowrap ${
                activeTab === 'staff'
                  ? 'border-app-accent text-app-accent'
                  : 'border-transparent text-app-text-secondary-light dark:text-app-text-secondary hover:text-app-text-light dark:hover:text-app-text'
              }`}
            >
              Staff
            </button>
          )}
          {/* CRM Tab - Only for dealer_admin */}
          {user?.role === 'dealer_admin' && (
            <button
              onClick={() => setActiveTab('crm')}
              className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 whitespace-nowrap ${
                activeTab === 'crm'
                  ? 'border-app-accent text-app-accent'
                  : 'border-transparent text-app-text-secondary-light dark:text-app-text-secondary hover:text-app-text-light dark:hover:text-app-text'
              }`}
            >
              CRM Leads
            </button>
          )}
          {/* Billing Tab - Only for dealer_admin */}
          {user?.role === 'dealer_admin' && (
            <button
              onClick={() => setActiveTab('billing')}
              className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 whitespace-nowrap ${
                activeTab === 'billing'
                  ? 'border-app-accent text-app-accent'
                  : 'border-transparent text-app-text-secondary-light dark:text-app-text-secondary hover:text-app-text-light dark:hover:text-app-text'
              }`}
            >
              💳 Billing
            </button>
          )}
        </div>

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <>
            {/* Check if user has analytics access */}
            {user?.role !== 'admin' && !billingStatus?.planLimits?.hasAnalytics ? (
              <div className="bg-white dark:bg-app-surface rounded-lg shadow-sm border border-app-border-light dark:border-app-border p-8 text-center">
                <div className="max-w-md mx-auto">
                  <div className="text-5xl mb-4">📊</div>
                  <h2 className="text-xl font-bold text-app-text-light dark:text-app-text mb-2">
                    Analytics Not Available
                  </h2>
                  <p className="text-app-text-secondary-light dark:text-app-text-secondary mb-6">
                    Your current plan doesn't include access to advanced analytics. Upgrade to Growth or Premium to unlock powerful insights about your dealership performance.
                  </p>
                  <div className="space-y-2 mb-6 text-left bg-app-bg-light dark:bg-app-bg rounded-lg p-4">
                    <p className="text-sm text-app-text-secondary-light dark:text-app-text-secondary font-semibold">
                      Analytics features include:
                    </p>
                    <ul className="text-sm text-app-text-secondary-light dark:text-app-text-secondary space-y-1 ml-4">
                      <li>• Real-time appointment metrics and KPIs</li>
                      <li>• Daily appointment trends and patterns</li>
                      <li>• Status funnel visualization</li>
                      <li>• Top performing vehicles</li>
                      <li>• Check-in time analytics</li>
                      <li>• Appointments by day of week</li>
                    </ul>
                  </div>
                  {user?.role === 'dealer_admin' && (
                    <button
                      onClick={() => setActiveTab('billing')}
                      className="px-6 py-2 bg-app-accent hover:bg-app-accent-hover text-white rounded transition-colors"
                    >
                      Upgrade Your Plan
                    </button>
                  )}
                </div>
              </div>
            ) : dashboardData ? (
          <DashboardLayout>
            {/* KPI Cards */}
            <KpiGrid>
              <KpiCard
                label={dashboardData.kpis.todayBookings.label}
                value={dashboardData.kpis.todayBookings.value}
                trend={dashboardData.kpis.todayBookings.trend}
                icon="calendar"
                color="blue"
              />
              <KpiCard
                label={dashboardData.kpis.showRate.label}
                value={dashboardData.kpis.showRate.value}
                trend={dashboardData.kpis.showRate.trend}
                icon="target"
                suffix="%"
                color="green"
              />
              <KpiCard
                label={dashboardData.kpis.completedToday.label}
                value={dashboardData.kpis.completedToday.value}
                trend={dashboardData.kpis.completedToday.trend}
                icon="check"
                color="purple"
              />
              <KpiCard
                label={dashboardData.kpis.noShowsToday.label}
                value={dashboardData.kpis.noShowsToday.value}
                trend={dashboardData.kpis.noShowsToday.trend}
                icon="x"
                color="orange"
              />
            </KpiGrid>

            {/* Main Charts */}
            <ChartsGrid columns={1}>
              <DailyAppointmentsChart data={dashboardData.dailyBookings} days={30} />
            </ChartsGrid>

            <ChartsGrid columns={2}>
              <StatusFunnel data={dashboardData.statusFunnel} />
              <AppointmentsByDayChart data={dashboardData.dayOfWeekData} />
            </ChartsGrid>

            <ChartsGrid columns={1}>
              <TopVehiclesTable data={dashboardData.topVehicles} />
            </ChartsGrid>

            <ChartsGrid columns={1}>
              <CheckInTimeChart data={dashboardData.checkInTimes} />
            </ChartsGrid>
          </DashboardLayout>
            ) : null}
          </>
        )}

        {/* Appointments Management Tab */}
        {activeTab === 'appointments' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
              <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-3 border border-app-border-light dark:border-app-border">
                <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary mb-1">Today</div>
                <div className="text-lg font-semibold text-app-text-light dark:text-app-text">
                  {todayStats.total}
                </div>
                <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary mt-1">
                  {todayStats.pending} pending • {todayStats.confirmed} confirmed
                </div>
              </div>
              <div className="bg-cyan-50 dark:bg-app-bg-secondary rounded-2xl p-3 border border-cyan-200 dark:border-app-border">
                <div className="text-[11px] text-cyan-600 dark:text-app-text-secondary mb-1">Vehicle Ready</div>
                <div className="text-lg font-semibold text-cyan-600 dark:text-cyan-400">
                  {allStats.vehicleReady}
                </div>
                <div className="text-[11px] text-cyan-600/70 dark:text-app-text-secondary mt-1">Cars waiting</div>
              </div>
              <div className="bg-purple-50 dark:bg-app-bg-secondary rounded-2xl p-3 border border-purple-200 dark:border-app-border">
                <div className="text-[11px] text-purple-600 dark:text-app-text-secondary mb-1">Checked In</div>
                <div className="text-lg font-semibold text-purple-600 dark:text-purple-400">
                  {allStats.checkedIn}
                </div>
                <div className="text-[11px] text-purple-600/70 dark:text-app-text-secondary mt-1">Active drives</div>
              </div>
              <div className="bg-orange-50 dark:bg-app-bg-secondary rounded-2xl p-3 border border-orange-200 dark:border-app-border">
                <div className="text-[11px] text-orange-600 dark:text-app-text-secondary mb-1">No Show</div>
                <div className="text-lg font-semibold text-orange-600 dark:text-orange-400">
                  {allStats.noShow}
                </div>
                <div className="text-[11px] text-orange-600/70 dark:text-app-text-secondary mt-1">All time</div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-3 border border-app-border-light dark:border-app-border space-y-2">
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary mb-1">Status</div>
                  <div className="flex flex-col gap-1">
                    {/* Top row: First 5 filters */}
                    <div className="flex gap-1">
                      {statusOptions.slice(0, 5).map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setStatusFilter(s)}
                          className={`px-3 py-1 rounded-full border text-[11px] ${
                            statusFilter === s
                              ? 'border-app-accent bg-app-input-light dark:bg-app-input text-app-accent'
                              : 'border-app-input-border-light dark:border-app-input-border bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text'
                          }`}
                        >
                          {s === 'all' ? 'All' : s}
                        </button>
                      ))}
                    </div>
                    {/* Bottom row: Completed, Canceled, No Show */}
                    <div className="flex gap-1">
                      {statusOptions.slice(5).map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setStatusFilter(s)}
                          className={`px-3 py-1 rounded-full border text-[11px] ${
                            statusFilter === s
                              ? 'border-app-accent bg-app-input-light dark:bg-app-input text-app-accent'
                              : 'border-app-input-border-light dark:border-app-input-border bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text'
                          }`}
                        >
                          {s === 'all' ? 'All' : s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary">From</label>
                  <input
                    type="date"
                    className="mt-1 w-full px-2 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary">To</label>
                  <input
                    type="date"
                    className="mt-1 w-full px-2 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  onClick={fetchAppointments}
                  className="px-3 py-2 rounded-xl bg-app-text-light dark:bg-app-text text-app-bg-light dark:text-app-bg font-semibold text-[11px]"
                >
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFrom('');
                    setTo('');
                    setStatusFilter('all');
                  }}
                  className="px-3 py-2 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text font-semibold text-[11px]"
                >
                  Clear filters
                </button>
              </div>
            </div>

            {/* Appointments list */}
            <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-3 border border-app-border-light dark:border-app-border space-y-2">
              <div className="text-sm font-semibold text-app-text-light dark:text-app-text">
                Appointments
              </div>

              {loading ? (
                <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary mt-2">
                  Loading appointments…
                </div>
              ) : filteredAppointments.length === 0 ? (
                <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary mt-2">
                  No appointments found for this filter.
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                  {filteredAppointments.map((b) => {
                    const dateLabel = b.date
                      ? new Date(b.date).toLocaleDateString()
                      : '';
                    const timeLabel = b.time || '';
                    const customerName = b.user?.name || 'Customer';
                    const vehicleSummary = b.vehicles
                      ?.map((v) => `${v.year} ${v.make} ${v.model}`)
                      .join(', ');

                    return (
                      <div
                        key={b._id}
                        className="bg-app-bg-light dark:bg-app-bg border border-app-border-light dark:border-app-border rounded-xl p-3 space-y-1"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="text-[12px] font-semibold text-app-text-light dark:text-app-text">
                              {customerName}
                            </div>
                            <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary">
                              {dateLabel} • {timeLabel}
                            </div>
                          </div>
                          <span
                            className={`text-[10px] px-2 py-1 rounded-full font-medium ${
                              b.status === 'Confirmed'
                                ? 'bg-green-100 dark:bg-green-600/20 text-green-700 dark:text-green-400'
                                : b.status === 'Pending'
                                ? 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-300'
                                : b.status === 'Vehicle Ready'
                                ? 'bg-cyan-100 dark:bg-cyan-600/20 text-cyan-700 dark:text-cyan-400'
                                : b.status === 'Checked In'
                                ? 'bg-purple-100 dark:bg-purple-600/20 text-purple-700 dark:text-purple-400'
                                : b.status === 'Completed'
                                ? 'bg-blue-100 dark:bg-blue-600/20 text-blue-700 dark:text-blue-400'
                                : b.status === 'Canceled'
                                ? 'bg-red-100 dark:bg-red-600/20 text-red-700 dark:text-red-400'
                                : b.status === 'No Show'
                                ? 'bg-orange-100 dark:bg-orange-600/20 text-orange-700 dark:text-orange-400'
                                : 'bg-gray-100 dark:bg-app-text-secondary/20 text-gray-600 dark:text-app-text-secondary'
                            }`}
                          >
                            {b.status}
                          </span>
                        </div>

                        {vehicleSummary && (
                          <div className="text-[11px] text-app-text-light dark:text-app-text">
                            {vehicleSummary}
                          </div>
                        )}

                        {b.user?.email && (
                          <div className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary">
                            {b.user.email}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {/* Verify Customer button - always visible for non-completed/canceled */}
                          {!['Completed', 'Canceled', 'No Show'].includes(b.status) && (
                            <button
                              type="button"
                              onClick={() => navigate(`/dealer/verify/${b._id}`)}
                              className="py-1.5 px-3 rounded-lg bg-indigo-500/80 text-white text-[11px] font-semibold"
                            >
                              Verify Customer
                            </button>
                          )}
                          {canConfirm(b) && (
                            <button
                              type="button"
                              disabled={updatingId === b._id}
                              onClick={() => updateStatus(b._id, 'Confirmed')}
                              className="flex-1 py-1.5 rounded-lg bg-app-accent text-black text-[11px] font-semibold disabled:opacity-60"
                            >
                              {updatingId === b._id ? 'Updating…' : 'Confirm'}
                            </button>
                          )}
                          {canMarkVehicleReady(b) && (
                            <button
                              type="button"
                              disabled={updatingId === b._id}
                              onClick={() => updateStatus(b._id, 'Vehicle Ready')}
                              className="flex-1 py-1.5 rounded-lg bg-cyan-500/80 text-black text-[11px] font-semibold disabled:opacity-60"
                            >
                              {updatingId === b._id ? 'Updating…' : 'Vehicle Ready'}
                            </button>
                          )}
                          {canCheckIn(b) && (
                            <button
                              type="button"
                              disabled={updatingId === b._id}
                              onClick={() => updateStatus(b._id, 'Checked In')}
                              className="flex-1 py-1.5 rounded-lg bg-purple-500/80 text-black text-[11px] font-semibold disabled:opacity-60"
                            >
                              {updatingId === b._id ? 'Updating…' : 'Check In'}
                            </button>
                          )}
                          {canComplete(b) && (
                            <button
                              type="button"
                              disabled={updatingId === b._id}
                              onClick={() => updateStatus(b._id, 'Completed')}
                              className="flex-1 py-1.5 rounded-lg bg-blue-500/80 text-black text-[11px] font-semibold disabled:opacity-60"
                            >
                              {updatingId === b._id ? 'Updating…' : 'Mark Completed'}
                            </button>
                          )}
                          {canCancel(b) && (
                            <button
                              type="button"
                              disabled={updatingId === b._id}
                              onClick={() => updateStatus(b._id, 'Canceled')}
                              className="flex-1 py-1.5 rounded-lg bg-red-600/80 text-white text-[11px] font-semibold disabled:opacity-60"
                            >
                              {updatingId === b._id ? 'Updating…' : 'Cancel'}
                            </button>
                          )}
                          {canMarkNoShow(b) && (
                            <button
                              type="button"
                              disabled={updatingId === b._id}
                              onClick={() => updateStatus(b._id, 'No Show')}
                              className="flex-1 py-1.5 rounded-lg bg-orange-600/80 text-white text-[11px] font-semibold disabled:opacity-60"
                            >
                              {updatingId === b._id ? 'Updating…' : 'No Show'}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* Vehicles Tab */}
        {activeTab === 'vehicles' && (
          <>
            <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl border border-app-border-light dark:border-app-border p-4">
              <h2 className="text-base font-semibold text-app-text-light dark:text-app-text mb-3">
                Vehicle Inventory Management
              </h2>
              <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mb-4">
                Manage your dealership's vehicle inventory. Toggle availability for
                test drives and bulk import/update vehicles using CSV.
              </p>

              <DealerVehiclesTable
                key={vehiclesRefreshKey}
                onUploadClick={() => setIsCsvModalOpen(true)}
              />
            </div>

            {/* CSV Upload Modal */}
            <VehicleCsvUploadModal
              isOpen={isCsvModalOpen}
              onClose={() => setIsCsvModalOpen(false)}
              onSuccess={() => {
                setIsCsvModalOpen(false);
                // Refresh vehicles list by incrementing key
                setVehiclesRefreshKey((prev) => prev + 1);
              }}
            />
          </>
        )}

        {/* Staff Tab - Only for dealer_admin */}
        {activeTab === 'staff' && user?.role === 'dealer_admin' && (
          <DealerStaffScreen />
        )}

        {/* CRM Settings Tab - Only for dealer_admin */}
        {activeTab === 'crm' && user?.role === 'dealer_admin' && (
          <DealerCrmSettingsScreen />
        )}

        {/* Billing Tab - Only for dealer_admin */}
        {activeTab === 'billing' && user?.role === 'dealer_admin' && (
          <DealerBillingScreen />
        )}
      </div>
    </div>
  );
};

export default DealerDashboardScreen;
